/* 템플릿 = 테마(색·글꼴·모서리) + build(ctx). build 는 일반 페이지 배열을 돌려준다.
   그래서 미리보기와 실제 편집 화면가 같은 데이터이고, 만든 뒤엔 AI 채팅·레이아웃 명령이 그대로 먹는다.
   사진은 인디포토 공간 태그대로 들어가고, 글 중 gen 표시가 붙은 것만 AI가 쓴다(미리보기에선 예시 문구). */
import { ROOM_EN, SAMPLE, SPACE_ORDER, THEMES, roomLabel, type Theme } from './constants';
import { layoutRoomPage } from './layout';
import { SAMPLE_PHOTOS } from './samples';
import { newId, type El, type GenSpec, type Page } from './types';

export interface PhotoMeta { photoId?: number; externalId?: string; siteId?: number }

export interface BuildCtx {
  site: string;
  grouped: Record<string, string[]>;
  rooms: string[];
  all: string[];
  ph: (room: string | null | undefined, i?: number) => string;
  pick: (n: number, room: string | null, from?: number, first?: string[]) => string[];
  preview: boolean;
  /** 고른 사진이 한 공간뿐이면 그 공간 */
  single: string | null;
  /** 공간마다 사진이 2장 이하 */
  sparse: boolean;
  count: (room: string) => number;
  collection?: boolean;
  meta?: Map<string, PhotoMeta>;
}

type Opt = Record<string, unknown> & {
  room?: string; title?: string; sub?: string; kicker?: string | ((i: number, room: string) => string);
  overlay?: string; focus?: string; concept?: string; preset?: string; subs?: number; start?: number;
};

/* ── 요소 만들기 도우미 ── */
type Partial0 = Omit<El, 'id' | 'z'> & { z?: number };
const IMG = (src: string, x: number, y: number, w: number, h: number, o?: Partial<El>): Partial0 => ({ type: 'image', x, y, w, h, src, ...(o || {}) });
const BOX = (bg: string, x: number, y: number, w: number, h: number, o?: Partial<El>): Partial0 => ({ type: 'shape', x, y, w, h, bg, ...(o || {}) });
const TXT = (text: string, x: number, y: number, w: number, h: number, o?: Partial<El>): Partial0 => ({
  type: 'text', x, y, w, h, text, fontSize: 16, bold: false, color: '#111827', align: 'left', ...(o || {}), // dq-ignore: 템플릿이 만드는 문서 색(문서 데이터)
});
/* AI가 채울 글 — 미리보기에선 예시 문구, 실제 생성에선 빈칸으로 두고 gen 을 보고 AI가 쓴다 */
const GEN = (c: BuildCtx, gen: GenSpec, sample: string, x: number, y: number, w: number, h: number, o?: Partial<El>) =>
  TXT(c.preview ? sample : '', x, y, w, h, { gen, sample, ...(o || {}) });

type Item = Partial0 | null | undefined | false | '' | Item[];
function mkPage(kind: string, bg: string, els: Item[], extra?: Partial<Page>): Page {
  const page: Page = { id: `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, kind, bg, elements: [], ...(extra || {}) };
  const flat: Partial0[] = [];
  const walk = (a: Item) => { if (Array.isArray(a)) a.forEach(walk); else if (a) flat.push(a); };
  els.forEach(walk);
  flat.forEach((e, i) => page.elements.push({ id: newId(), z: i + 1, ...e }));
  return page;
}
const numKicker = (i: number, room: string) => `${String(i + 1).padStart(2, '0')}  ·  ${ROOM_EN[room] || 'SPACE'}`;
const kick = (o: Opt, i: number, room: string) => (typeof o.kicker === 'function' ? o.kicker(i, room) : o.kicker);

/* ── 페이지 빌더 ── */
function coverFull(c: BuildCtx, t: Theme, o: Opt) {
  const flat = o.overlay === 'flat';
  return mkPage('cover', t.alt, [
    IMG(c.ph(o.room, 0), 0, 0, 720, 960),
    flat ? BOX('rgba(17,24,39,.55)', 0, 660, 720, 300) : BOX('linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.72) 100%)', 0, 480, 720, 480), // dq-ignore: 템플릿이 만드는 문서 색(문서 데이터)
    !!o.kicker && TXT(String(o.kicker), 48, 682, 624, 24, { fontSize: 13, bold: true, color: 'rgba(255,255,255,.82)', ls: '.2em', font: t.bodyFont }), // dq-ignore: 템플릿이 만드는 문서 색(문서 데이터)
    TXT(o.title || '', 48, 716, 624, 100, { fontSize: 40, bold: true, color: '#ffffff', font: t.headFont }), // dq-ignore: 템플릿이 만드는 문서 색(문서 데이터)
    TXT(o.sub || '', 48, 812, 624, 50, { fontSize: 15, color: 'rgba(255,255,255,.85)', font: t.bodyFont }), // dq-ignore: 템플릿이 만드는 문서 색(문서 데이터)
  ]);
}
function coverSplit(c: BuildCtx, t: Theme, o: Opt) {
  return mkPage('cover', t.bg, [
    BOX(t.alt, 0, 0, 720, 420),
    TXT(String(o.kicker || 'INTERIOR CASE STUDY'), 56, 92, 608, 24, { fontSize: 13, bold: true, color: t.accent, ls: '.22em' }),
    TXT(o.title || '', 56, 126, 608, 150, { fontSize: 44, bold: true, color: t.onAlt, font: t.headFont, lh: '1.25' }),
    TXT(o.sub || '', 56, 300, 608, 60, { fontSize: 15, color: t.onAltMuted, font: t.bodyFont }),
    IMG(c.ph(o.room, 0), 0, 420, 720, 540),
  ]);
}
function coverMinimal(c: BuildCtx, t: Theme, o: Opt) {
  return mkPage('cover', t.bg, [
    IMG(c.ph(o.room, 0), 48, 48, 624, 620, { radius: t.radius || 0 }),
    TXT(String(o.kicker || 'INTERIOR CASE STUDY'), 48, 706, 420, 22, { fontSize: 12, bold: true, color: t.muted, ls: '.24em' }),
    TXT(o.title || '', 48, 736, 624, 110, { fontSize: 42, bold: true, color: t.head, font: t.headFont, lh: '1.2' }),
    TXT(o.sub || '', 48, 862, 440, 50, { fontSize: 14, color: t.text, font: t.bodyFont }),
    TXT(`${c.rooms.length} SPACES`, 500, 866, 172, 30, { fontSize: 12, bold: true, color: t.muted, align: 'right', ls: '.14em' }),
  ]);
}
function coverFrame(c: BuildCtx, t: Theme, o: Opt) {
  return mkPage('cover', t.bg, [
    TXT(String(o.kicker || 'INTERIOR CASE STUDY'), 60, 58, 600, 24, { fontSize: 12, bold: true, color: t.accent, ls: '.32em', align: 'center' }),
    BOX(t.accent, 60, 104, 600, 620),
    BOX(t.bg, 62, 106, 596, 616),
    IMG(c.ph(o.room, 0), 74, 118, 572, 592),
    TXT(o.title || '', 40, 752, 640, 70, { fontSize: 38, bold: true, color: t.head, font: t.headFont, align: 'center' }),
    BOX(t.accent, 330, 836, 60, 2),
    TXT(o.sub || '', 60, 856, 600, 40, { fontSize: 14, color: t.text, align: 'center', font: t.bodyFont }),
  ]);
}
function coverArch(c: BuildCtx, t: Theme, o: Opt) {
  return mkPage('cover', t.bg, [
    BOX(t.tint, 80, 60, 560, 660, { radius: 280 }),
    IMG(c.ph(o.room, 0), 110, 90, 500, 600, { radius: 250 }),
    TXT(String(o.kicker || 'NATURAL LIVING'), 60, 744, 600, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.26em', align: 'center' }),
    TXT(o.title || '', 40, 772, 640, 64, { fontSize: 38, bold: true, color: t.head, font: t.headFont, align: 'center' }),
    TXT(o.sub || '', 60, 846, 600, 40, { fontSize: 14, color: t.text, align: 'center', font: t.bodyFont }),
  ]);
}
/* 콜라주 표지 — 위에 제목, 아래를 사진 3장(왼쪽 크게 · 오른쪽 둘)으로 페이지 끝까지 */
function coverCollage(c: BuildCtx, t: Theme, o: Opt) {
  const shots = o.room ? c.pick(3, o.room, 0) : c.pick(3, null, 0, c.rooms.map((r) => c.ph(r, 0)));
  const TOP = 330, G = 6, LW = 440, RH = (960 - TOP - G) / 2;
  return mkPage('cover', t.bg, [
    TXT(String(o.kicker || 'INTERIOR CASE STUDY'), 56, 64, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.26em', font: t.bodyFont }),
    TXT(o.title || '', 56, 96, 608, 130, { fontSize: 42, bold: true, color: t.head, font: t.headFont, lh: '1.22' }),
    TXT(o.sub || '', 56, 240, 608, 40, { fontSize: 15, color: t.text, font: t.bodyFont }),
    BOX(t.accent, 56, 292, 44, 3),
    IMG(shots[0], 0, TOP, LW, 960 - TOP),
    IMG(shots[1], LW + G, TOP, 720 - LW - G, RH),
    IMG(shots[2], LW + G, TOP + RH + G, 720 - LW - G, RH),
  ]);
}
/* 타이포 표지 — 강조 면 위에 큰 제목, 아래쪽을 사진 한 장으로 */
function coverTypo(c: BuildCtx, t: Theme, o: Opt) {
  const PY = 560;
  return mkPage('cover', t.alt, [
    TXT(String(o.kicker || 'INTERIOR CASE STUDY'), 56, 72, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.3em', font: t.bodyFont }),
    TXT(o.title || '', 56, 110, 608, 280, { fontSize: 58, bold: true, color: t.onAlt, font: t.headFont, lh: '1.12' }),
    BOX(t.accent, 56, 412, 64, 4),
    TXT(o.sub || '', 56, 436, 608, 60, { fontSize: 16, color: t.onAltMuted, font: t.bodyFont, lh: '1.6' }),
    IMG(c.ph(o.room, 0), 0, PY, 720, 960 - PY),
  ]);
}
/* 현장 소개 — 소개 글 아래를 사진 3장으로 페이지 끝까지 채운다 */
function overviewPage(c: BuildCtx, t: Theme, o: Opt) {
  // 전체시공 템플릿이어도 고른 사진이 한 공간뿐이면 그 공간 소개로 쓴다(다른 공간·집 전체 이야기 X)
  const focus = o.focus || c.single, label = focus ? roomLabel(focus) : '';
  const r = c.rooms;
  // 사진이 적으면(한 공간 6장 미만, 또는 공간마다 2장 이하) 현장 소개 페이지는 빼서 같은 사진이 여러 번 나오지 않게 한다
  if (!o.focus && ((c.single && c.all.length < 6) || c.sparse)) return null;
  const strip = focus ? c.pick(3, focus, 1) : c.pick(3, null, 0, [c.ph(r[0], 1), c.ph(r[1] || r[0], 0), c.ph(r[2] || r[0], 0)]);
  const spaces = focus ? `${label} 집중 소개` : r.map((x) => roomLabel(x)).join('  ·  ') + (o.concept ? `   |   ${o.concept} 컨셉` : '');
  const G = 6, TOP = 452, LW = 420, RW = 720 - LW - G, RH = (960 - TOP - G) / 2;
  return mkPage('overview', t.bg, [
    TXT(String(o.kicker || 'OVERVIEW'), 56, 56, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.22em' }),
    TXT(o.title || '현장 소개', 56, 82, 608, 56, { fontSize: 32, bold: true, color: t.head, font: t.headFont }),
    TXT(spaces, 56, 142, 608, 26, { fontSize: 13.5, bold: true, color: t.muted, font: t.bodyFont }),
    GEN(c, { kind: 'overview', focus }, focus ? SAMPLE.overviewFocus(label) : c.preview ? SAMPLE.overview : SAMPLE.overviewRooms(r), 56, 184, 608, 244,
      { fontSize: 15.5, color: t.text, font: t.bodyFont, lh: '1.7' }),
    IMG(strip[0], 0, TOP, LW, 960 - TOP),
    IMG(strip[1], LW + G, TOP, RW, RH),
    IMG(strip[2], LW + G, TOP + RH + G, RW, RH),
  ]);
}
/* 공간 페이지 — 역할(main/sub/heading/body/kicker)만 정해두고 위치는 layoutRoomPage 프리셋에 맡긴다 */
function roomPage(c: BuildCtx, t: Theme, room: string, i: number, o: Opt) {
  const have = c.count(room);
  const nSub = Math.max(0, Math.min(o.subs ?? 3, have - 1));
  const rad = t.radius || 0;
  const els: Partial0[] = [IMG(c.ph(room, 0), 0, 0, 10, 10, { role: 'main', radius: rad })];
  for (let k = 1; k <= nSub; k++) els.push(IMG(c.ph(room, k), 0, 0, 10, 10, { role: 'sub', radius: rad ? Math.min(rad, 12) : 0 }));
  const k0 = kick(o, i, room);
  if (k0) els.push(TXT(k0, 0, 0, 10, 10, { role: 'kicker', fontSize: 12, bold: true, color: t.accent, ls: '.18em' }));
  els.push(TXT(roomLabel(room), 0, 0, 10, 10, { role: 'heading', fontSize: 30, bold: true, color: t.head, font: t.headFont }));
  els.push(GEN(c, { kind: 'roomBody', room }, SAMPLE.room[room] || SAMPLE.room.ETC, 0, 0, 10, 10, { role: 'body', fontSize: 15.5, color: t.text, font: t.bodyFont, lh: '1.7' }));
  const page = mkPage('room', t.bg, els, { room });
  layoutRoomPage(page, o.preset || 'hero');
  return page;
}
/* 디테일 페이지 — 대표 사진(0번)을 뺀 사진 중 start번부터 최대 4장 */
function detailPage(c: BuildCtx, t: Theme, room: string, o: Opt) {
  const total = c.count(room), rad = t.radius ? Math.min(t.radius, 16) : 0;
  let idx: number[] = [];
  for (let k = o.start || 1; k < total && idx.length < 4; k++) idx.push(k);
  if (idx.length < 2) idx = [];
  if (!idx.length) for (let k = 1; k < total && idx.length < 4; k++) idx.push(k);
  if (!idx.length) return null; // 대표 사진 한 장뿐이면 디테일 페이지는 만들지 않는다
  const X = 24, Y = 150, W = 672, H = 684, G = 6;
  const cells: number[][] = idx.length === 1 ? [[X, Y, W, H]]
    : idx.length === 2 ? [[X, Y, (W - G) / 2, H], [X + (W + G) / 2, Y, (W - G) / 2, H]]
    : idx.length === 3 ? [[X, Y, W, (H - G) * 0.55], [X, Y + (H - G) * 0.55 + G, (W - G) / 2, (H - G) * 0.45], [X + (W + G) / 2, Y + (H - G) * 0.55 + G, (W - G) / 2, (H - G) * 0.45]]
    : [0, 1, 2, 3].map((k) => [X + (k % 2) * ((W + G) / 2), Y + Math.floor(k / 2) * ((H + G) / 2), (W - G) / 2, (H - G) / 2]);
  return mkPage('detail', t.bg, [
    TXT(String(o.kicker || 'DETAIL'), 56, 62, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.22em' }),
    TXT(`${roomLabel(room)} 디테일`, 56, 90, 608, 56, { fontSize: 30, bold: true, color: t.head, font: t.headFont }),
    idx.map((k, j) => { const [x, y, w, h] = cells[j].map(Math.round); return IMG(c.ph(room, k), x, y, w, h, { radius: rad }); }),
    GEN(c, { kind: 'detail', room }, SAMPLE.detail, 56, 850, 608, 70, { fontSize: 15, color: t.text, font: t.bodyFont, lh: '1.7' }),
  ]);
}
function closingPage(c: BuildCtx, t: Theme, o: Opt) {
  const r = o.room || c.rooms[c.rooms.length - 1];
  return mkPage('closing', t.alt, [
    IMG(c.ph(r, 1), 0, 0, 720, 600),
    TXT(o.title || '감사합니다', 56, 636, 608, 60, { fontSize: 36, bold: true, color: t.onAlt, font: t.headFont }),
    TXT(`${c.site} 시공사례를 끝까지 봐주셔서 감사합니다. 우리 집에도 이런 변화가 필요하시다면 편하게 상담을 요청해 주세요.`, 56, 704, 608, 96,
      { fontSize: 15.5, color: t.onAltMuted, font: t.bodyFont, lh: '1.7' }),
    BOX(t.accent, 56, 822, 48, 3),
    TXT('상담 문의  ·  업체명과 연락처를 입력하세요', 56, 840, 608, 32, { fontSize: 13.5, bold: true, color: t.onAlt }),
  ]);
}
/* 공간 목차 — 실린 공간을 번호·이름·대표 사진 썸네일로 (글은 AI가 쓰지 않음) */
function indexPage(c: BuildCtx, t: Theme, o: Opt) {
  const rs = c.rooms.slice(0, 6);
  if (!rs.length) return null;
  const TOP = 196, rowH = Math.min(124, (960 - 48 - TOP) / rs.length), rad = t.radius ? Math.min(t.radius, 12) : 0;
  return mkPage('index', t.bg, [
    TXT(String(o.kicker || 'CONTENTS'), 56, 64, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.26em', font: t.bodyFont }),
    TXT(o.title || '공간 목차', 56, 92, 608, 56, { fontSize: 32, bold: true, color: t.head, font: t.headFont }),
    BOX(t.head, 56, 164, 608, 2),
    rs.map((r, i) => {
      const y = TOP + i * rowH;
      return [
        TXT(String(i + 1).padStart(2, '0'), 56, y + rowH / 2 - 22, 70, 44, { fontSize: 30, bold: true, color: t.accent, font: t.headFont }),
        TXT(roomLabel(r), 136, y + rowH / 2 - 26, 300, 34, { fontSize: 22, bold: true, color: t.head, font: t.headFont }),
        TXT(ROOM_EN[r] || 'SPACE', 136, y + rowH / 2 + 8, 300, 22, { fontSize: 11.5, bold: true, color: t.muted, ls: '.2em' }),
        IMG(c.ph(r, 0), 512, y + 10, 152, rowH - 20, { radius: rad }),
        BOX(t.tint, 56, y + rowH - 1, 608, 1),
      ];
    }),
  ]);
}
/* 사진 모아보기 — 공간마다 앞쪽 사진을 돌아가며 최대 9장. 4장보다 적으면 만들지 않는다 */
function galleryPage(c: BuildCtx, t: Theme, o: Opt) {
  const list: string[] = [], seen = new Set<string>();
  for (let k = 0; k < 3 && list.length < 9; k++)
    c.rooms.forEach((r) => { const u = (c.grouped[r] || [])[k]; if (u && !seen.has(u) && list.length < 9) { seen.add(u); list.push(u); } });
  if (c.preview) while (list.length < 9 && c.all.length) list.push(c.all[list.length % c.all.length]);
  if (c.preview && !c.all.length) while (list.length < 9) list.push('');
  if (list.length < 4 || c.sparse) return null;
  const TOP = 168, G = 6, cols = 3, rows = Math.ceil(list.length / cols), ch = (960 - TOP - G * (rows - 1)) / rows;
  const els: Partial0[] = [
    TXT(String(o.kicker || 'GALLERY'), 56, 56, 608, 22, { fontSize: 12, bold: true, color: t.accent, ls: '.26em', font: t.bodyFont }),
    TXT(o.title || '사진 모아보기', 56, 84, 608, 56, { fontSize: 32, bold: true, color: t.head, font: t.headFont }),
  ];
  for (let r = 0; r < rows; r++) {
    const rowItems = list.slice(r * cols, r * cols + cols), cw = (720 - G * (rowItems.length - 1)) / rowItems.length;
    rowItems.forEach((u, i) => els.push(IMG(u, i * (cw + G), TOP + r * (ch + G), cw, ch)));
  }
  return mkPage('gallery', t.bg, els);
}

/* ── 템플릿 목록 ── cat: whole 전체시공 · kitchen 주방 · bath 욕실 · style 스타일 / focus: 그 공간 사진만 쓰는 템플릿 */
export interface Template {
  id: string; name: string; cat: 'whole' | 'kitchen' | 'bath' | 'style'; theme: string; tags: string[]; desc: string;
  focus?: string; style?: string;
  build: (c: BuildCtx, t: Theme) => (Page | null | false)[];
}
const rooms = (c: BuildCtx, f: (r: string, i: number) => Page | null) => c.rooms.map(f);

export const TEMPLATES: Template[] = [
  { id:'basic', name:'기본 공간 스토리', cat:'whole', theme:'basic', tags:['기본형','공간별'],
    desc:'표지 다음에 공간마다 한 페이지씩, 대표 사진을 위에 크게 보여주는 기본 구성이에요.',
    build:(c,t)=>[ coverFull(c,t,{ title:`${c.site} 시공사례`, sub:'AI가 자동으로 정리한 공간별 스토리', overlay:'flat' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'hero' })) ] },
  { id:'magazine', name:'매거진 스토리', cat:'whole', theme:'magazine', tags:['매거진','가운데형'],
    desc:'잡지 표지처럼 시작해서 현장 소개를 하고, 공간마다 대표 사진을 가운데에 크게 보여줘요.',
    build:(c,t)=>[ coverSplit(c,t,{ title:`${c.site}\n리모델링 스토리`, sub:'공간마다 바뀐 이야기를 담았습니다' }), overviewPage(c,t,{}),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'center', kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'portfolio', name:'포트폴리오 그리드', cat:'whole', theme:'portfolio', tags:['그리드','사진 많이'],
    desc:'사진을 격자로 시원하게 보여주는 포트폴리오형. 사진이 많은 현장에 잘 맞아요.',
    build:(c,t)=>[ coverMinimal(c,t,{ title:`${c.site}\n포트폴리오`, sub:'공간별 시공 사진 모음' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'grid', subs:5 })), closingPage(c,t,{}) ] },
  { id:'collage', name:'콜라주 스토리', cat:'whole', theme:'sand', tags:['콜라주','목차','띠형'],
    desc:'사진 3장 콜라주 표지와 공간 목차로 시작해서, 공간마다 글을 먼저 읽고 사진을 크게 보는 구성이에요.',
    build:(c,t)=>[ coverCollage(c,t,{ title:`${c.site}\n시공 이야기`, sub:'공간마다 달라진 모습을 차례로 보여드려요' }), indexPage(c,t,{}),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'band', kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'brochure', name:'프리미엄 브로슈어', cat:'whole', theme:'navyGold', tags:['브로슈어','명조체','사이드형'],
    desc:'네이비·골드 타이포 표지와 세로로 긴 대표 사진으로 고급 분양 브로슈어 같은 느낌을 내요.',
    build:(c,t)=>[ coverTypo(c,t,{ kicker:'PREMIUM INTERIOR', title:`${c.site}\n시공사례`, sub:'완성도 높은 마감을 공간별로 소개합니다' }), overviewPage(c,t,{}),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'side', subs:2, kicker:numKicker })), galleryPage(c,t,{}), closingPage(c,t,{}) ] },
  { id:'gallery', name:'갤러리 북', cat:'whole', theme:'gallery', tags:['갤러리','사진 모아보기'],
    desc:'흰 바탕에 사진을 크게, 마지막에 사진 모아보기 한 장으로 현장 전체를 한눈에 보여줘요.',
    build:(c,t)=>[ coverFull(c,t,{ kicker:'PHOTO GALLERY', title:`${c.site}`, sub:'사진으로 보는 시공 현장' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'hero' })), galleryPage(c,t,{}), closingPage(c,t,{}) ] },
  { id:'blog', name:'블로그 세로형', cat:'whole', theme:'mint', tags:['블로그','띠형','읽기 쉬움'],
    desc:'네이버 블로그·오늘의집에 올리기 좋게 글이 먼저, 사진이 페이지 폭 끝까지 이어지는 세로형이에요.',
    build:(c,t)=>[ coverSplit(c,t,{ kicker:'REMODELING DIARY', title:`${c.site}\n시공 후기`, sub:'공간마다 어떻게 바뀌었는지 정리했어요' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'band' })), closingPage(c,t,{}) ] },

  { id:'kitchen-focus', name:'주방 집중 리포트', cat:'kitchen', focus:'KITCHEN', theme:'kitchen', tags:['주방','반쪽형'],
    desc:'주방 하나만 깊게 — 표지부터 소개, 대표 사진, 디테일 컷까지 주방 사진으로만 구성해요.',
    build:(c,t)=>[ coverFull(c,t,{ room:'KITCHEN', kicker:'KITCHEN REMODELING', title:`${c.site} 주방`, sub:'동선과 수납을 새로 정리한 주방 이야기' }),
                   overviewPage(c,t,{ focus:'KITCHEN' }), roomPage(c,t,'KITCHEN',0,{ preset:'split', subs:4 }),
                   detailPage(c,t,'KITCHEN',{ start:1 }), closingPage(c,t,{ room:'KITCHEN' }) ] },
  { id:'kitchen-book', name:'키친 디테일 북', cat:'kitchen', focus:'KITCHEN', theme:'kitchenWarm', tags:['주방','디테일'],
    desc:'따뜻한 톤으로 주방의 디테일 컷을 여러 장 보여주는 구성이에요. 주방 사진이 많을수록 좋아요.',
    build:(c,t)=>[ coverMinimal(c,t,{ room:'KITCHEN', kicker:'KITCHEN DETAIL BOOK', title:`${c.site}\n주방 디테일`, sub:'매일 쓰는 주방을 자세히 보여드려요' }),
                   roomPage(c,t,'KITCHEN',0,{ preset:'center', subs:4 }), detailPage(c,t,'KITCHEN',{ start:1 }),
                   (c.preview || c.count('KITCHEN')>=6) && detailPage(c,t,'KITCHEN',{ start:5, kicker:'MORE DETAIL' }),
                   closingPage(c,t,{ room:'KITCHEN' }) ] },
  { id:'kitchen-modern', name:'모던 키친 매거진', cat:'kitchen', focus:'KITCHEN', theme:'charcoal', tags:['주방','다크','그리드'],
    desc:'차콜 바탕에 골드 포인트, 큰 타이포 표지와 사진 격자로 주방을 세련되게 보여줘요.',
    build:(c,t)=>[ coverTypo(c,t,{ room:'KITCHEN', kicker:'MODERN KITCHEN', title:`${c.site}\n주방`, sub:'동선과 수납, 마감까지 새로 정리한 주방' }),
                   roomPage(c,t,'KITCHEN',0,{ preset:'grid', subs:5 }), detailPage(c,t,'KITCHEN',{ start:1 }), closingPage(c,t,{ room:'KITCHEN' }) ] },

  { id:'bath-focus', name:'욕실 집중 리포트', cat:'bath', focus:'BATHROOM', theme:'bath', tags:['욕실','반쪽형'],
    desc:'욕실 사진만으로 표지·소개·대표 사진·디테일을 구성해요. 욕실 리모델링 현장에 딱 맞아요.',
    build:(c,t)=>[ coverSplit(c,t,{ room:'BATHROOM', kicker:'BATHROOM REMODELING', title:`${c.site}\n욕실 리모델링`, sub:'매일 쓰기 편한 욕실로 바꾼 이야기' }),
                   overviewPage(c,t,{ focus:'BATHROOM' }), roomPage(c,t,'BATHROOM',0,{ preset:'split', subs:4 }),
                   detailPage(c,t,'BATHROOM',{ start:1 }), closingPage(c,t,{ room:'BATHROOM' }) ] },
  { id:'bath-hotel', name:'호텔식 욕실', cat:'bath', focus:'BATHROOM', theme:'hotel', tags:['욕실','다크'],
    desc:'어두운 바탕에 골드 포인트로 호텔 같은 분위기를 내는 욕실 템플릿이에요.',
    build:(c,t)=>[ coverFrame(c,t,{ room:'BATHROOM', kicker:'HOTEL STYLE BATHROOM', title:`${c.site} 욕실`, sub:'호텔처럼 머무르고 싶은 욕실' }),
                   roomPage(c,t,'BATHROOM',0,{ preset:'center', subs:4 }), detailPage(c,t,'BATHROOM',{ start:1 }), closingPage(c,t,{ room:'BATHROOM' }) ] },
  { id:'bath-spa', name:'스파 욕실', cat:'bath', focus:'BATHROOM', theme:'sage', tags:['욕실','세이지','사이드형'],
    desc:'세이지 그린과 둥근 아치 사진으로 스파처럼 편안한 욕실을 보여줘요.',
    build:(c,t)=>[ coverArch(c,t,{ room:'BATHROOM', kicker:'SPA BATHROOM', title:`${c.site} 욕실`, sub:'하루의 피로를 푸는 욕실' }), overviewPage(c,t,{ focus:'BATHROOM' }),
                   roomPage(c,t,'BATHROOM',0,{ preset:'side', subs:2 }), detailPage(c,t,'BATHROOM',{ start:1 }), closingPage(c,t,{ room:'BATHROOM' }) ] },
  { id:'bath-white', name:'화이트 욕실 북', cat:'bath', focus:'BATHROOM', theme:'aqua', tags:['욕실','화이트','콜라주'],
    desc:'콜라주 표지에 맑은 화이트·아쿠아 톤으로 깔끔한 욕실을 담아요.',
    build:(c,t)=>[ coverCollage(c,t,{ room:'BATHROOM', kicker:'CLEAN BATHROOM', title:`${c.site}\n욕실 리모델링`, sub:'매일 쓰기 편하고 관리 쉬운 욕실' }),
                   roomPage(c,t,'BATHROOM',0,{ preset:'band', subs:3 }), detailPage(c,t,'BATHROOM',{ start:1 }), closingPage(c,t,{ room:'BATHROOM' }) ] },

  { id:'organic', name:'오가닉 내추럴', cat:'style', style:'organic', theme:'organic', tags:['오가닉','둥근 사진'],
    desc:'부드러운 베이지·올리브 톤에 둥근 사진으로 자연스럽고 편안한 무드를 담아요.',
    build:(c,t)=>[ coverArch(c,t,{ title:`${c.site} 시공사례`, sub:'자연스럽고 편안한 우리 집' }), overviewPage(c,t,{ concept:'오가닉' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'center' })), closingPage(c,t,{}) ] },
  { id:'minimal', name:'미니멀 화이트', cat:'style', style:'minimal', theme:'minimal', tags:['미니멀','여백'],
    desc:'흰 여백과 검은 글자만으로 정돈된 느낌을 주는 미니멀 템플릿이에요.',
    build:(c,t)=>[ coverMinimal(c,t,{ kicker:'MINIMAL HOUSE', title:`${c.site}`, sub:'덜어내고 정돈한 공간' }), overviewPage(c,t,{ concept:'미니멀' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'split', kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'wood', name:'모던 우드', cat:'style', style:'wood', theme:'wood', tags:['모던 우드','따뜻한 톤'],
    desc:'우드 브라운 포인트로 따뜻하고 차분한 모던 무드를 내는 템플릿이에요.',
    build:(c,t)=>[ coverSplit(c,t,{ kicker:'MODERN WOOD', title:`${c.site}\n시공사례`, sub:'따뜻한 톤으로 채운 우리 집' }), overviewPage(c,t,{ concept:'모던 우드' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'hero' })), closingPage(c,t,{}) ] },
  { id:'industrial', name:'인더스트리얼 로프트', cat:'style', style:'industrial', theme:'industrial', tags:['인더스트리얼','다크','그리드'],
    desc:'짙은 차콜 바탕에 오렌지 포인트, 사진을 격자로 크게 보여주는 개성 있는 템플릿이에요.',
    build:(c,t)=>[ coverFull(c,t,{ kicker:'INDUSTRIAL LOFT', title:`${c.site}`, sub:'개성 있게 완성한 공간' }), overviewPage(c,t,{ concept:'인더스트리얼' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'grid', subs:3, kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'classic', name:'클래식 프렌치', cat:'style', style:'classic', theme:'classic', tags:['클래식','명조체','액자'],
    desc:'크림 바탕에 골드 액자, 명조체 제목으로 우아한 분위기를 내는 클래식 템플릿이에요.',
    build:(c,t)=>[ coverFrame(c,t,{ title:`${c.site} 시공사례`, sub:'우아하게 완성한 공간 이야기' }), overviewPage(c,t,{ concept:'클래식' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'center' })), closingPage(c,t,{}) ] },
  { id:'nordic', name:'북유럽 라이트', cat:'style', style:'nordic', theme:'nordic', tags:['북유럽','밝은 톤','반쪽형'],
    desc:'밝은 그레이·블루 톤으로 포근하고 환한 북유럽 무드를 담는 템플릿이에요.',
    build:(c,t)=>[ coverMinimal(c,t,{ kicker:'NORDIC HOME', title:`${c.site}\n시공사례`, sub:'밝고 포근한 우리 집' }), overviewPage(c,t,{ concept:'북유럽' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'split', kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'japandi', name:'재팬디 젠', cat:'style', style:'japandi', theme:'japandi', tags:['재팬디','차분한 톤','사이드형'],
    desc:'오트밀·차콜 톤과 세로로 긴 사진으로 낮고 차분한 재팬디 무드를 담아요.',
    build:(c,t)=>[ coverMinimal(c,t,{ kicker:'JAPANDI HOME', title:`${c.site}`, sub:'비우고 채운 차분한 집' }), overviewPage(c,t,{ concept:'재팬디' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'side', subs:2, kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'vintage', name:'빈티지 레트로', cat:'style', style:'vintage', theme:'vintage', tags:['빈티지','액자','띠형'],
    desc:'머스터드·틸 컬러와 액자 표지로 정겨운 레트로 감성을 내요.',
    build:(c,t)=>[ coverFrame(c,t,{ kicker:'VINTAGE MOOD', title:`${c.site} 시공사례`, sub:'오래 머물고 싶은 따뜻한 집' }), overviewPage(c,t,{ concept:'빈티지' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'band', kicker:numKicker })), closingPage(c,t,{}) ] },
  { id:'pastel', name:'파스텔 코지', cat:'style', style:'pastel', theme:'pastel', tags:['파스텔','둥근 사진','목차'],
    desc:'핑크·라일락 파스텔과 둥근 사진, 공간 목차로 사랑스럽고 아늑한 분위기를 내요.',
    build:(c,t)=>[ coverArch(c,t,{ kicker:'COZY PASTEL', title:`${c.site} 시공사례`, sub:'부드럽고 아늑한 우리 집' }), indexPage(c,t,{}),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'center' })), closingPage(c,t,{}) ] },
  { id:'midcentury', name:'미드센추리 모던', cat:'style', style:'midcentury', theme:'midcentury', tags:['미드센추리','콜라주','갤러리'],
    desc:'오렌지·딥틸 포인트와 콜라주 표지, 사진 모아보기로 경쾌한 미드센추리 무드를 보여줘요.',
    build:(c,t)=>[ coverCollage(c,t,{ kicker:'MID-CENTURY MODERN', title:`${c.site}\n시공사례`, sub:'경쾌한 색과 단정한 선으로 채운 집' }), overviewPage(c,t,{ concept:'미드센추리' }),
                   ...rooms(c,(r,i)=> roomPage(c,t,r,i,{ preset:'grid', subs:3, kicker:numKicker })), galleryPage(c,t,{}), closingPage(c,t,{}) ] },
];
export const CAT_LABEL: Record<string, string> = { whole: '전체시공', kitchen: '주방', bath: '욕실', style: '스타일' };
export const templateById = (id?: string | null) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];

/* ── 빌드 컨텍스트: 공간별 사진 묶음 + 사진 고르기(ph) ──
   shift: 미리보기에서만 템플릿마다 사진을 몇 장씩 밀어서 홈 카드들이 같은 사진으로 보이지 않게 한다 */
export function makeBuildCtx(grouped: Record<string, string[]>, site: string, preview: boolean, shift = 0): BuildCtx {
  const rooms = SPACE_ORDER.filter((r) => grouped[r]);
  const all = rooms.flatMap((r) => grouped[r]);
  const ph = (room: string | null | undefined, i = 0) => {
    const list = room && grouped[room] && grouped[room].length ? grouped[room] : all;
    return list.length ? list[(i + shift) % list.length] : '';
  };
  /* 서로 다른 사진 n장 — first → room 사진(from번째부터) → 나머지 공간 사진 순 */
  const pick = (n: number, room: string | null, from = 0, first?: string[]) => {
    const seen = new Set<string>(), out: string[] = [];
    const add = (u: string) => { if (u && !seen.has(u) && out.length < n) { seen.add(u); out.push(u); } };
    (first || []).forEach(add);
    if (room && grouped[room]) grouped[room].slice(from).forEach(add);
    all.forEach(add);
    while (out.length < n) out.push(out[out.length % Math.max(1, out.length)] || ''); // 사진이 정말 모자랄 때만 반복
    return out;
  };
  const single = !preview && rooms.length === 1 ? rooms[0] : null;
  const sparse = !preview && rooms.length > 0 && rooms.every((r) => grouped[r].length <= 2);
  return { site, grouped, rooms, all, ph, pick, preview, single, sparse, count: (room) => (grouped[room] || []).length || (preview ? 4 : 0) };
}

/** 사진이 모자라 못 만든 페이지(null)는 빼고, 사진 요소에 인디포토 사진 번호를 붙인다 */
export function buildTemplatePages(tpl: Template, ctx: BuildCtx): Page[] {
  const pages = tpl.build(ctx, THEMES[tpl.theme]).filter((p): p is Page => !!p);
  if (ctx.meta) pages.forEach((p) => p.elements.forEach((e) => {
    if (e.type !== 'image' || !e.src) return;
    const m = ctx.meta!.get(e.src);
    if (m) { e.photoId = m.photoId; e.externalId = m.externalId; e.siteId = m.siteId; }
  }));
  return pages;
}

/* 미리보기 — 새로 만든 예시 사진(samples.ts)으로. 회사·외부 사진은 쓰지 않는다 */
const PREVIEW_CACHE: Record<string, Page[]> = {};
export function previewPages(tpl: Template): Page[] {
  if (!PREVIEW_CACHE[tpl.id]) PREVIEW_CACHE[tpl.id] = buildTemplatePages(tpl, makeBuildCtx(SAMPLE_PHOTOS, '우리집', true, TEMPLATES.indexOf(tpl) * 3));
  return PREVIEW_CACHE[tpl.id];
}

/* 템플릿의 공간별 사진 칸 수 — 공간마다 사진이 넉넉하다고 치고 한 번 만들어 본 뒤, 쓰인 서로 다른 사진 수를 센다 */
export function templateSlots(tpl: Template, roomList: string[]): Record<string, number> {
  const g: Record<string, string[]> = {};
  roomList.forEach((r) => (g[r] = Array.from({ length: 12 }, (_, k) => `probe:${r}:${k}`)));
  const cap: Record<string, Set<string>> = {};
  roomList.forEach((r) => (cap[r] = new Set()));
  buildTemplatePages(tpl, makeBuildCtx(g, '우리집', false)).forEach((p) => p.elements.forEach((e) => {
    if (e.type !== 'image' || !/^probe:/.test(e.src || '')) return;
    const [, r, k] = (e.src || '').split(':');
    cap[r]?.add(k);
  }));
  return Object.fromEntries(roomList.map((r) => [r, cap[r].size]));
}

export const aiTextCount = (list: Page[]) => list.reduce((n, p) => n + p.elements.filter((e) => e.gen).length, 0);
export const PAGE_KIND_LABEL: Record<string, string> = { cover: '표지', overview: '현장 소개', room: '공간 페이지', detail: '디테일', closing: '마무리', index: '공간 목차', gallery: '사진 모아보기' };
export function pageKindLabel(p: Page) {
  if (p.kind === 'room') return `${roomLabel(p.room)} 소개`;
  return (p.kind && PAGE_KIND_LABEL[p.kind]) || '페이지';
}
